import {
  email,
  required,
  useGetIdentity,
  useGetOne,
  useTranslate,
  ValidationError,
} from "ra-core";
import { TriangleAlert } from "lucide-react";
import { useEffect } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { NumberInput } from "@/components/admin/number-input";
import { DateInput } from "@/components/admin/date-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";

import { getSaleRole } from "../providers/commons/canAccess";
import type { AppRole, Sale } from "../types";
import {
  CLIENT_TYPE_CHOICES,
  MARITAL_PROPERTY_REGIME_CHOICES,
  MARITAL_STATUS_CHOICES,
} from "./clientChoices";
import {
  validateChildrenCount,
  validateClientBirthday,
  validateClientCurp,
  validateClientMaritalRegime,
  validateClientRfc,
} from "./clientValidation";
import { useRfcDuplicateCheck } from "./useRfcDuplicateCheck";

// Only these roles may reassign a client's owner; everyone else keeps the
// default (the current user on create, the existing owner on edit).
const OWNER_PICKER_ROLES: AppRole[] = ["direccion", "lead_manager"];

const saleOptionRenderer = (choice: Sale) =>
  `${choice.first_name} ${choice.last_name}`;

/**
 * Client create/edit form fields (Almikia Epic E3, MEMORY.md).
 *
 * ASSUMPTION: the linked-contact and owner pickers below are plain
 * `<SelectInput>` (not a free-text `<AutocompleteInput>`) precisely because
 * `<SelectInput>` never calls the data provider with the user's typed text as
 * a filter — the AML client-identification PII rule bans client field values
 * from ever reaching a data-provider request query string, and a name search
 * filter would do exactly that. See the open question this leaves (no name
 * search on these pickers) in the E3 status notes.
 *
 * ASSUMPTION: the contact picker below is capped at `perPage={200}`, sorted by
 * last name, instead of the default 25-per-page/no-sort — with no typed-filter
 * search allowed (see above), a demo/small org fits comfortably under 200, but
 * an org with more contacts than that will not see the rest in this picker.
 */
export const ClientInputs = () => (
  <div className="flex flex-col gap-4">
    <SelectInput
      source="client_type"
      choices={CLIENT_TYPE_CHOICES}
      validate={required()}
      helperText={false}
      defaultValue="persona_fisica"
    />
    <ClientFullLegalNameInput />
    <div className="flex flex-col gap-2">
      <TextInput
        source="rfc"
        parse={(value?: string) => (value ? value.toUpperCase() : value)}
        validate={validateClientRfc}
        helperText={false}
      />
      <ClientRfcDuplicateWarning />
    </div>
    <TextInput
      source="curp"
      parse={(value?: string) => (value ? value.toUpperCase() : value)}
      validate={validateClientCurp}
      helperText={false}
    />
    <DateInput
      source="birthday"
      validate={validateClientBirthday}
      helperText={false}
    />
    <SelectInput
      source="marital_status"
      choices={MARITAL_STATUS_CHOICES}
      helperText={false}
    />
    <ClientMaritalPropertyRegimeInput />
    <NumberInput
      source="children_count"
      min={0}
      step={1}
      validate={validateChildrenCount}
      helperText={false}
    />
    <TextInput source="email" validate={email()} helperText={false} />
    <TextInput source="phone" helperText={false} />
    <TextInput source="address" helperText={false} />
    <ReferenceInput
      source="contact_id"
      reference="contacts"
      sort={{ field: "last_name", order: "ASC" }}
      perPage={200}
    >
      <SelectInput
        label="resources.clients.fields.contact_id"
        helperText={false}
      />
    </ReferenceInput>
    <ClientOwnerInput />
  </div>
);

const ClientFullLegalNameInput = () => {
  const translate = useTranslate();
  return (
    <TextInput
      source="full_legal_name"
      validate={required()}
      helperText={translate("resources.clients.helper.full_legal_name", {
        _: "Write it exactly as on the official ID",
      })}
    />
  );
};

/**
 * Non-blocking duplicate-RFC warning (Almikia Epic E3b, owner answer A7):
 * "Duplicate RFC is not blocked; show a warning". Reveals nothing about the
 * other client -- no name, owner, id or link, just the fact that the RFC is
 * already registered. Icon + text (`role="status"`/`aria-live="polite"`), so
 * the state is never conveyed by color alone (design-system.md section 4:
 * text never carries the brand color, and the semantic green/red pair is
 * reserved for binary ready/not-ready states, which this isn't) and is
 * announced without stealing focus or blocking the form.
 */
const ClientRfcDuplicateWarning = () => {
  const translate = useTranslate();
  const isDuplicate = useRfcDuplicateCheck();

  if (!isDuplicate) return null;

  return (
    <p
      role="status"
      aria-live="polite"
      className="text-foreground flex items-center gap-1.5 text-sm"
    >
      <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
      {translate("resources.clients.warnings.rfc_duplicate", {
        _: "This RFC is already registered for another client.",
      })}
    </p>
  );
};

/**
 * Marital property regime (Almikia Epic E3b, owner answer A8). Visible and
 * required only while marital status is casado; hidden and cleared to `null`
 * for any other status, so switching away from casado never submits a stale
 * regime value even before the `cleanupClientForSave` defense-in-depth runs.
 *
 * `<SelectInput>` never renders its own inline validation error (see
 * DealInputs.tsx's `DealPosiInputs` for the same limitation on the deal
 * stage), so the required-when-casado message is rendered here manually
 * instead of widening that shared component's behavior for every select in
 * the app.
 */
const ClientMaritalPropertyRegimeInput = () => {
  const { control, setValue } = useFormContext();
  const maritalStatus = useWatch({ control, name: "marital_status" });
  const isCasado = maritalStatus === "casado";
  const { errors } = useFormState({
    control,
    name: "marital_property_regime",
  });
  const errorMessage = errors.marital_property_regime?.message as
    | string
    | undefined;

  useEffect(() => {
    if (!isCasado) {
      setValue("marital_property_regime", null, { shouldDirty: false });
    }
  }, [isCasado, setValue]);

  if (!isCasado) return null;

  return (
    <div className="flex flex-col gap-1">
      <SelectInput
        source="marital_property_regime"
        choices={MARITAL_PROPERTY_REGIME_CHOICES}
        validate={validateClientMaritalRegime}
        helperText={false}
      />
      {errorMessage && (
        <p role="alert" className="text-brand-red-text text-sm -mt-3">
          <ValidationError error={errorMessage} />
        </p>
      )}
    </div>
  );
};

/**
 * Owner (`sales_id`) picker. Only direccion and lead_manager can reassign a
 * client's owner; every other role never sees this input at all, so
 * `sales_id` is submitted unchanged from the form's `defaultValues` (the
 * current user on create, the existing owner on edit — react-hook-form keeps
 * an unregistered `defaultValues` key in the submitted data).
 */
const ClientOwnerInput = () => {
  const { identity } = useGetIdentity();
  const { data: currentSale } = useGetOne<Sale>("sales", {
    id: identity?.id,
  });

  if (!currentSale || !OWNER_PICKER_ROLES.includes(getSaleRole(currentSale))) {
    return null;
  }

  return (
    <ReferenceInput
      source="sales_id"
      reference="sales"
      filter={{ "disabled@neq": true }}
      sort={{ field: "last_name", order: "ASC" }}
    >
      <SelectInput
        label="resources.clients.fields.sales_id"
        optionText={saleOptionRenderer}
        validate={required()}
        helperText={false}
      />
    </ReferenceInput>
  );
};
